import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { ButtonComponent } from '@shared/components/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { WORKFLOW_NAMES } from '@shared/store/workflow/workflow.const';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';

const wfNamesToPendoIds = new Map<string, string>([
  ['Confirm Investigator Data', 'confirm-patient-data-toolbar'],
  ['Confirm Trial Timeline', 'confirm-trial-timeline-toolbar'],
  ['Forecast Methodology', 'forecast-methodology-toolbar'],
  ['Review Invoices', 'review-invoices-toolbar'],
  ['Site, Patient, & Custom Curves', 'site-patient-and-custom-curves-toolbar'],
]);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconComponent, ButtonComponent, TooltipDirective],
  selector: 'aux-workflow-panel-general-item',
  templateUrl: './workflow-panel-general-item.component.html',
})
export class WorkflowPanelGeneralItemComponent {
  workflowQuery = inject(WorkflowQuery);
  workflowService = inject(WorkflowService);

  workflowName = input.required<string>();

  currentWorkflow = computed(() => {
    return this.workflowQuery.getWorkflowByStepType(this.workflowName())();
  });
  isLocked = computed(() => {
    return !!this.currentWorkflow()?.properties?.locked;
  });
  getBtnDataPendoId = computed(() => {
    const currentWorkflow = this.currentWorkflow();
    const isLocked = this.isLocked();

    if (!currentWorkflow) {
      return '';
    }

    const id = wfNamesToPendoIds.get(currentWorkflow.name);

    if (!id) {
      return '';
    }

    return isLocked ? `${id}-unlock` : `${id}-lock`;
  });
  getBtnIcon = computed(() => {
    return this.isLocked() ? 'LockOpen' : 'Lock';
  });
  workflowTitle = computed(() => {
    const currentWorkflow = this.currentWorkflow();

    if (!currentWorkflow) {
      return '';
    }

    const workflowName = WORKFLOW_NAMES[currentWorkflow.step_type];

    if (workflowName.includes('Confirm ')) {
      const finalNameIndex = 1;

      return workflowName.split('Confirm ')[finalNameIndex];
    }

    if (workflowName.includes('Review ')) {
      const finalNameIndex = 1;

      return workflowName.split('Review ')[finalNameIndex];
    }

    return workflowName;
  });
  getBtnLabel = computed(() => {
    const currentWorkflow = this.currentWorkflow();
    const text = currentWorkflow?.properties?.locked ? 'Unlock' : 'Lock';

    return `${text} ${this.workflowTitle()}`;
  });

  hasUserPermissions = input<boolean>(false);

  getBtnTooltipText = computed(() => {
    return !this.hasUserPermissions() ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION : '';
  });
  getBtnVariant = computed(() => {
    return this.isLocked() ? 'secondary' : 'primary';
  });
  getTitle = computed(() => {
    const isLocked = this.isLocked();
    const currentWorkflow = this.currentWorkflow();

    if (!currentWorkflow) {
      return '';
    }

    if (!isLocked) {
      return 'Unlocked - You must lock this section before closing your month.';
    }

    return `Locked by ${currentWorkflow.updatedAuthorFullName} on ${currentWorkflow.update_date}`;
  });

  isWorkflowNameVisible = input<boolean>(true);
  lockAvailable = input<boolean>(true);

  onToggleLockWorkflow() {
    const currentWorkflow = this.currentWorkflow();

    if (!currentWorkflow) {
      return;
    }

    this.workflowService
      .changeLockStatus(!currentWorkflow.properties.locked, currentWorkflow, false)
      .catch(console.error);
  }
}
